import React, { useEffect, useState } from "react";
import { Menu, User, UserPlus, X, MapPin } from "lucide-react";
import { NAV_LINKS, PARK } from "@/lib/site";
import { getCustomerToken } from "@/lib/customerAuth";
import { useContent } from "@/lib/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // "Pick Your Pitch" nav tab — only when Justina switches the interactive
  // pitch map on (Admin → Content → Website features).
  const c = useContent();
  const links = c("pitchmap.enabled") === "1"
    ? [...NAV_LINKS, { href: "/pick-your-pitch", label: "Pick Your Pitch", highlight: true }]
    : NAV_LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setSignedIn(!!getCustomerToken());
    const onStorage = () => setSignedIn(!!getCustomerToken());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F5EFE6]/85 border-b border-[#DDD1BE]/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-28 md:h-32">
        <a
          href="#top"
          data-testid="nav-brand"
          className="flex items-center gap-3 leading-none group"
          aria-label="Walnut Lakes"
        >
          <img
            src={PARK.logo}
            srcSet={`${PARK.logo} 1x, ${PARK.logo} 2x`}
            alt="Walnut Lakes"
            width="480"
            height="160"
            className="h-24 md:h-32 w-auto logo-blend logo-hd transition-transform duration-500 group-hover:scale-[1.04]"
            loading="eager"
            decoding="async"
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={
                l.highlight
                  ? "inline-flex items-center gap-1.5 whitespace-nowrap font-brand text-base tracking-[0.14em] uppercase transition-colors rounded-full border px-3.5 py-1.5 " +
                    (scrolled
                      ? "border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white"
                      : "border-[#E4B2A8]/70 text-[#E4B2A8] hover:bg-[#B76E79] hover:border-[#B76E79] hover:text-white")
                  : `font-brand text-base tracking-[0.14em] uppercase transition-colors ${
                      scrolled ? "text-[#14100F] hover:text-[#B76E79]" : "text-white/90 hover:text-[#E4B2A8]"
                    }`
              }
            >
              {l.highlight && <MapPin size={13} />}{l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {signedIn ? (
            <a
              href="/account"
              data-testid="nav-account-link"
              className={`inline-flex items-center gap-1.5 font-brand tracking-[0.14em] uppercase text-xs transition-colors ${
                scrolled ? "text-[#14100F] hover:text-[#B76E79]" : "text-white/90 hover:text-[#E4B2A8]"
              }`}
              aria-label="My account"
            >
              <User size={14} /> My Account
            </a>
          ) : (
            <div
              className="relative group"
              data-testid="nav-account-menu"
              onMouseEnter={() => setAccountOpen(true)}
              onMouseLeave={() => setAccountOpen(false)}
            >
              <button
                type="button"
                onClick={() => setAccountOpen((v) => !v)}
                data-testid="nav-account-trigger"
                className={`inline-flex items-center gap-1.5 font-brand tracking-[0.14em] uppercase text-xs transition-colors py-2 ${
                  scrolled ? "text-[#14100F] hover:text-[#B76E79]" : "text-white/90 hover:text-[#E4B2A8]"
                }`}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                <User size={14} /> My Account
              </button>
              <div
                role="menu"
                className={`absolute right-0 top-full min-w-[180px] bg-[#F5EFE6] border border-[#DDD1BE] rounded-lg shadow-xl overflow-hidden transition-all duration-150 origin-top-right ${
                  accountOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1 pointer-events-none"
                }`}
              >
                <a
                  href="/account/login"
                  role="menuitem"
                  data-testid="nav-signin-link"
                  className="flex items-center gap-2 px-4 py-3 text-[#14100F] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors font-brand tracking-[0.14em] uppercase text-xs border-b border-[#DDD1BE]"
                >
                  <User size={14} /> Sign in
                </a>
                <a
                  href="/account/register"
                  role="menuitem"
                  data-testid="nav-signup-link"
                  className="flex items-center gap-2 px-4 py-3 text-[#14100F] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors font-brand tracking-[0.14em] uppercase text-xs"
                >
                  <UserPlus size={14} /> Sign up
                </a>
              </div>
            </div>
          )}
          <a
            href="#book"
            data-testid="nav-book-cta"
            className={scrolled ? "btn-primary font-brand tracking-[0.14em]" : "btn-ghost-light font-brand tracking-[0.14em]"}
          >
            Book Now
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`md:hidden p-2 rounded-full ${
            scrolled ? "text-[#14100F]" : "text-white"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#F5EFE6] border-t border-[#DDD1BE] px-6 py-6"
        >
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className={`font-brand uppercase tracking-[0.14em] text-lg py-2 ${
                  l.highlight ? "inline-flex items-center gap-2 text-[#B76E79]" : "text-[#14100F]"
                }`}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.highlight && <MapPin size={15} />}{l.label}
              </a>
            ))}
            {signedIn ? (
              <a
                href="/account"
                onClick={() => setOpen(false)}
                className="font-brand uppercase tracking-[0.14em] text-[#14100F] text-lg py-2 inline-flex items-center gap-2"
                data-testid="mobile-account-link"
              >
                <User size={16} /> My Account
              </a>
            ) : (
              <div className="border-t border-[#DDD1BE] pt-3 mt-1">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] mb-1 flex items-center gap-1.5">
                  <User size={11} /> My Account
                </p>
                <a
                  href="/account/login"
                  onClick={() => setOpen(false)}
                  className="block font-brand uppercase tracking-[0.14em] text-[#14100F] text-base py-2 pl-1"
                  data-testid="mobile-signin-link"
                >
                  Sign in
                </a>
                <a
                  href="/account/register"
                  onClick={() => setOpen(false)}
                  className="block font-brand uppercase tracking-[0.14em] text-[#14100F] text-base py-2 pl-1"
                  data-testid="mobile-signup-link"
                >
                  Sign up
                </a>
              </div>
            )}
            <a
              href="#book"
              onClick={() => setOpen(false)}
              className="btn-primary justify-center mt-2 font-brand tracking-[0.14em]"
              data-testid="mobile-book-cta"
            >
              Book Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
